import { MS_PER_DAY } from '@klareospace/pharos-lib-types';
import type { TenantFreeTrial, TimeSlot } from '@klareospace/pharos-lib-types';

/**
 * Far-future sentinel date, used to sort entries with a missing/undefined time value last in
 * ascending-order comparisons (e.g. delivery/pickup stops without a time slot).
 */
export const FAR_FUTURE_DATE = new Date('9999-12-31T23:59:59.999Z');

/** True when a time slot's end is not strictly after its start (invalid/inverted range). */
export function isInvertedTimeSlot(slot: TimeSlot): boolean {
  return new Date(slot.end).getTime() <= new Date(slot.start).getTime();
}

export function toDate(value: Date | string | null | undefined): Date | undefined {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
}

/** Adds `days` local calendar days to `date`, returning a new `Date` (negative `days` subtracts). */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function getStartOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Last calendar day (1-31) of a given UTC year/month (1-indexed month). */
export function lastDayOfMonthUtc(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Yesterday (D-1) as a UTC-midnight date, computed against the UTC calendar day of `today`. */
export function toUtcYesterday(today: Date): Date {
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1));
}

/**
 * The planned end of a tenant's free trial — `trial.startedAt + trial.durationDays` calendar days,
 * computed in UTC milliseconds (not local-calendar arithmetic) so it stays correct regardless of
 * the host's timezone.
 */
export function resolveTrialPlannedEnd(trial: TenantFreeTrial): Date {
  return new Date(trial.startedAt.getTime() + trial.durationDays * MS_PER_DAY);
}

/** Whether a tenant's recorded free trial has not yet reached its planned end. */
export function isTrialRunning(trial: TenantFreeTrial, now: Date): boolean {
  return now < resolveTrialPlannedEnd(trial);
}

/** UTC midnight (00:00:00.000) of `date`'s calendar day. */
export function toUtcStartOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/** Last UTC millisecond (23:59:59.999) of `date`'s calendar day. */
export function toUtcEndOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
}

/** Whether `a` and `b` fall on the same UTC calendar day. */
export function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  );
}

/** Encodes a local calendar day as the UTC `Date` with the same year/month/day, at UTC midnight. */
export function toUtcCalendarDate(localDate: Date): Date {
  return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()));
}

/** Inverse of {@link toUtcCalendarDate} — reads a UTC calendar day and returns the local `Date` at local midnight. */
export function fromUtcCalendarDate(utcDate: Date): Date {
  return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
}

/** `timeZone`'s UTC offset (minutes, positive east of UTC) in effect at `utcInstant` — DST-aware via `Intl`. */
function getTimeZoneOffsetMinutes(utcInstant: Date, timeZone: string): number {
  const parts = getPartsInTimeZone(utcInstant, timeZone);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return (asUtc - utcInstant.getTime()) / (60 * 1000);
}

const WALL_CLOCK_TIME_REGEX = /^\d{2}:\d{2}$/;

/**
 * Converts a local `'HH:MM'` wall-clock time, on `timeZone`'s calendar day of `now`, into a UTC
 * `Date`. The UTC offset is resolved at the *resulting* instant, not the naive one: a first pass
 * reads the offset at the naive instant (treating the wall-clock value as if it were already UTC),
 * then a second, authoritative pass re-reads the offset at the instant that first offset produces —
 * which lands on the correct side of a DST transition. Policy for the DST gap/overlap edge case: a
 * wall-clock time that falls inside the skipped (spring-forward) or repeated (fall-back) hour is
 * resolved using whichever offset this two-pass read happens to land on, without further
 * disambiguation.
 */
function wallClockTodayToUtc(now: Date, hhmm: string, timeZone: string): Date {
  if (!WALL_CLOCK_TIME_REGEX.test(hhmm)) {
    throw new Error(`[wallClockTodayToUtc] Invalid time '${hhmm}' (expected 'HH:MM').`);
  }
  const [hours, minutes] = hhmm.split(':').map((part) => Number.parseInt(part, 10));
  if (hours > 23 || minutes > 59) {
    throw new Error(`[wallClockTodayToUtc] Invalid time '${hhmm}' (expected 'HH:MM').`);
  }

  const today = getPartsInTimeZone(now, timeZone);
  const naiveUtc = Date.UTC(Number(today.year), Number(today.month) - 1, Number(today.day), hours, minutes, 0);
  const naiveOffsetMinutes = getTimeZoneOffsetMinutes(new Date(naiveUtc), timeZone);
  const offsetMinutes = getTimeZoneOffsetMinutes(new Date(naiveUtc - naiveOffsetMinutes * 60 * 1000), timeZone);
  return new Date(naiveUtc - offsetMinutes * 60 * 1000);
}

/** Parses a local `'HH:MM-HH:MM'` wall-clock slot, on `timeZone`'s calendar day of `now`, into a UTC `TimeSlot`. */
export function parseWallClockTimeSlotToday(slot: string, now: Date, timeZone: string): TimeSlot {
  const [startRaw, endRaw] = slot.split('-');
  if (!startRaw || !endRaw) {
    throw new Error(`[parseWallClockTimeSlotToday] Invalid slot '${slot}' (expected 'HH:MM-HH:MM').`);
  }
  return { end: wallClockTodayToUtc(now, endRaw, timeZone), start: wallClockTodayToUtc(now, startRaw, timeZone) };
}

/** Formats the Y/M/D/H/M/S components of `date` as seen in `timeZone`, independent of the host's own timezone. */
function getPartsInTimeZone(date: Date, timeZone: string): Record<string, string> {
  const formatter = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  });
  return Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
}
